import {
  ChakraBox,
  ChakraFlex,
  EmptyListV2,
  FAIconName,
} from '@equip.health/ui';
import DocumentsHeader from './DocumentsHeader';
import DocumentsTableV2 from './DocumentsTableV2';
import {
  PatientDocumentsFilters,
  useDeletePatientDocumentMutation,
  useGetAllPatientDocumentsInfiniteQuery,
} from '~/store/services/documents';
import DocumentsFilter from './DocumentsFilter';
import { useCallback, useState } from 'react';
import {
  DOCUMENT_TABLE_EMPTY_STATE,
  NO_RESULTS_FOUND,
  NO_RESULTS_WITH_FILTERS_VIEW,
} from '~/lib/constants/documentCenter.constants';
import VoidDocumentModal from '../../common/VoidDocumentModal';
import { useDocumentUpload } from '~/lib/hooks/usePatientDocumentUpload';
import UploadConfirmationModal from '../../common/UploadConfirmationModal';
import DocumentDropZone from './DocumentDropZone';
import UnlinkNoteBeforeVoidModal from './UnlinkNoteBeforeVoidModal';
import { navigateToViewNote } from '~/lib/util/patientDocuments.util';
import { useHistory } from 'react-router-dom';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface PatientDocumentsV2Props {
  patientId: string;
}

const TOP_LEVEL_ID = 'patient-documents-v2';

const PatientDocumentsV2 = ({ patientId }: PatientDocumentsV2Props) => {
  const [selectedDocument, setSelectedDocument] =
    useState<PatientDocumentV2 | null>(null);
  const [isVoidModalOpen, setIsVoidModalOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isUploadSuccessModalOpen, setIsUploadSuccessModalOpen] =
    useState<boolean>(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<PatientDocumentsFilters>({
    documentTypes: [],
    uploadedBy: [],
    fileFormats: [],
    uploadStartDate: '',
    uploadEndDate: '',
  });

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileUploadErrorMessages, setFileUploadErrorMessages] = useState<
    string[]
  >([]);
  const { trackEvent } = useAnalytics();
  const history = useHistory();
  const { openFileExplorer, handleFiles } = useDocumentUpload({
    onUploadSuccess: (files, modalErrorMessages) => {
      if (files.length === 0) return;
      setUploadedFiles(files);
      setFileUploadErrorMessages(modalErrorMessages);
      setIsUploadSuccessModalOpen(true);
    },
    patientId,
  });

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useGetAllPatientDocumentsInfiniteQuery(
      {
        filters,
        patientId,
        searchTerm,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );
  const [deletePatientDocument, deletePatientDocumentResult] =
    useDeletePatientDocumentMutation();
  const documents = data?.pages.flatMap((page) => page.data) ?? [];
  const count = data?.pages[data.pages.length - 1]?.count ?? 0;

  const handleStartOver = () => {
    setSearchTerm('');
    setFilters({
      documentTypes: [],
      fileFormats: [],
      uploadedBy: [],
      uploadStartDate: '',
      uploadEndDate: '',
    });
  };
  const onLoadMoreDocuments = useCallback(async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  const onSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleVoidClick = (document: PatientDocumentV2) => {
    setSelectedDocument(document);
    if (document?.noteIds?.length > 0) {
      setIsUnlinkModalOpen(true);
    } else {
      setIsVoidModalOpen(true);
    }
  };

  const handleVoidConfirm = (reasonId: string) => {
    if (!selectedDocument) return;
    deletePatientDocument({
      documentExternalId: selectedDocument.documentExternalId,
      patientExternalId: patientId,
      voidReasonCode: reasonId,
    })
      .unwrap()
      .then(() => {
        setIsVoidModalOpen(false);
        setSelectedDocument(null);
      })
      .catch(() => {
        setIsVoidModalOpen(true);
      });
  };

  const handleVoidCancel = () => {
    deletePatientDocumentResult.reset();
    setIsVoidModalOpen(false);
    setSelectedDocument(null);
  };

  const handleViewNote = () => {
    navigateToViewNote(history, selectedDocument);
    setIsUnlinkModalOpen(false);
    setSelectedDocument(null);
  };

  const hasActiveFilters =
    filters.documentTypes.length > 0 ||
    filters.uploadedBy.length > 0 ||
    filters.fileFormats.length > 0 ||
    filters.uploadStartDate !== '' ||
    filters.uploadEndDate !== '';
  const shouldShowFilters =
    (count > 0 || searchTerm || hasActiveFilters) && patientId;

  const hasSearchOrFilters = searchTerm || hasActiveFilters;
  return (
    <>
      <ChakraBox paddingBottom="24px" paddingTop="12px">
        <ChakraFlex flexDirection="column" gap="24px">
          <DocumentsHeader
            count={count}
            id={`${TOP_LEVEL_ID}__documents-header`}
            isLoading={isLoading || isFetching}
            patientId={patientId}
          />
          {shouldShowFilters && (
            <DocumentsFilter
              filters={filters}
              id={`${TOP_LEVEL_ID}__documents-filter`}
              onFiltersChange={setFilters}
              onSearch={onSearch}
              patientId={patientId}
              value={searchTerm}
            />
          )}
          {!isLoading && (
            <DocumentDropZone
              isDisabled={isUploadSuccessModalOpen}
              onFilesDrop={handleFiles}
            >
              {count > 0 ? (
                <DocumentsTableV2
                  documents={documents}
                  id={TOP_LEVEL_ID}
                  isLoading={isLoading || isFetching}
                  onLoadMoreDocuments={onLoadMoreDocuments}
                  onVoidClick={handleVoidClick}
                />
              ) : (
                <ChakraBox marginTop="40px">
                  <EmptyListV2
                    buttonProps={{
                      leftIcon: hasSearchOrFilters
                        ? FAIconName.redoRegular
                        : FAIconName.fileArrowUpRegular,
                      onClick: hasSearchOrFilters
                        ? handleStartOver
                        : () => {
                            trackEvent(
                              DocumentCenterEvents.emptyListViewUploadDocumentButtonClicked,
                              {
                                patientId,
                                Source: AnalyticsSourceValues.DOCUMENT_CENTER,
                              },
                            );
                            openFileExplorer();
                          },
                      text: hasSearchOrFilters
                        ? 'Start over'
                        : 'Upload documents',
                    }}
                    id={`${TOP_LEVEL_ID}__empty-list`}
                    primaryText={
                      hasSearchOrFilters
                        ? NO_RESULTS_FOUND
                        : DOCUMENT_TABLE_EMPTY_STATE
                    }
                    secondaryText={
                      hasSearchOrFilters ? NO_RESULTS_WITH_FILTERS_VIEW : ''
                    }
                    showBorder={!hasSearchOrFilters}
                  />
                </ChakraBox>
              )}
            </DocumentDropZone>
          )}
        </ChakraFlex>
      </ChakraBox>
      {isUploadSuccessModalOpen && (
        <UploadConfirmationModal
          id={`${TOP_LEVEL_ID}__upload-confirmation-modal`}
          isOpen={isUploadSuccessModalOpen}
          onClose={() => setIsUploadSuccessModalOpen(false)}
          patientId={patientId}
          uploadedFiles={uploadedFiles}
          uploadErrorMessages={fileUploadErrorMessages}
        />
      )}
      {selectedDocument && (
        <VoidDocumentModal
          document={selectedDocument}
          id={TOP_LEVEL_ID}
          isErrorToastVisible={deletePatientDocumentResult.isError}
          isLoading={deletePatientDocumentResult.isLoading}
          isOpen={isVoidModalOpen}
          onDeleteClose={handleVoidCancel}
          onDeleteConfirm={handleVoidConfirm}
        />
      )}
      {selectedDocument && (
        <UnlinkNoteBeforeVoidModal
          isOpen={isUnlinkModalOpen}
          onClose={() => {
            setIsUnlinkModalOpen(false);
            setSelectedDocument(null);
          }}
          onViewNoteClick={() => {
            handleViewNote();
          }}
        />
      )}
    </>
  );
};

export default PatientDocumentsV2;
